/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  AppAlert,
  ConfirmModal,
  TermAndServiceContent,
} from "@advapacs/common";
import { TFunction } from "i18next";
import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { SignUpAction } from "@src/pages/signup/ducks/slices";
import { EulaAction } from "@src/reducers/slices/eulaSlice";
import { useAppDispatch } from "@src/config/store";

interface TermAndServiceModalProps {
  t: TFunction;
  isPublic?: boolean;
  acceptAction?: () => void;
  closeAction?: () => void;
}

export const TermAndServiceModal: FC<TermAndServiceModalProps> = ({
  t,
  isPublic,
  acceptAction,
  closeAction,
}) => {
  const dispatch = useAppDispatch();
  const { showSignUpEulaAgreementInfo } = useSelector(
    (state: IRootState) => state.signUpSlice
  );
  const { errorMessage, eula, acceptEula } = useSelector(
    (state: IRootState) => state.eulaReducer
  );
  const { isAuthenticated } = useSelector(
    (state: IRootState) => state.loginReducer
  );

  const showWithPublicOrAuthenticated = useMemo(() => {
    return isPublic || isAuthenticated;
  }, [isPublic, isAuthenticated]);

  return (
    <>
      {errorMessage && (
        <AppAlert.ErrorAlert
          error={errorMessage}
          dismissible
          onClose={() => dispatch(EulaAction.setErrorMessage(null))}
        />
      )}
      {eula &&
        (showSignUpEulaAgreementInfo ||
          (showWithPublicOrAuthenticated && !acceptEula)) && (
          <ConfirmModal
            size="lg"
            show={
              showSignUpEulaAgreementInfo ||
              (showWithPublicOrAuthenticated && !acceptEula)
            }
            onClose={() => {
              dispatch(SignUpAction.showSignUpEulaAgreementInfo(false));
            }}
            header={t("modal:eula.eula_cp_header")}
            body={
              <>
                <TermAndServiceContent
                  content={eula}
                  title={isPublic ? "" : t("modal:eula.eula_pp_title")}
                />
              </>
            }
            cancelBtnName={
              showSignUpEulaAgreementInfo ? t("close") : t("button.decline")
            }
            confirmBtnVariant="primary"
            confirmBtnName={t("button.accept")}
            cancelBtnVariant="light"
            confirm={isPublic ? undefined : acceptAction}
            cancel={closeAction}
          />
        )}
    </>
  );
};
